import { friendlyError, PublicLinkError, readData, retrying, setVisitSort, sleep, subscribeVisit, visitSort, type LinkPage } from 'features/public-pages/public/publicApi';
import type { TableSort } from 'features/filters/public/useFilterPane';
import { VISUAL_DATA_ENDPOINTS } from '../contracts/endpoints';
import type { VisualShellManifestResponse } from '../canvas/visualShells';

/**
 * A native page opened by public link is drawn by the same canvas as any page (docs/architecture/pages.md, 4.5). Only where it reads differs:
 * the page shell and each visual's data come from the link's anonymous routes (`/api/public/pages/<token>/...`), whose bodies are the
 * positive allowlists of `app/shared/public_page_allowlist.py`, and are shaped here like the answers the canvas already reads. The canvas
 * names the page as `LINK_REPORT_ID`; the token itself stays in this module and is never put in a request the canvas makes.
 *
 * The visuals a canvas refresh reads without a window or view (cards, charts) are read together, in one request per tick, as
 * `visualReadBatch` reads a signed-in page: a page view spends one of the link's requests, not one per visual. Every read waits out the
 * server's throttling (`retrying`); a read still refused shows the busy sentence where its visual would be. A visual the server ran out of
 * time for (`too_slow`) is asked once more (`readGroup`).
 *
 * PreviewPage's host draws a native page the same way, from its snapshot alone (`showPreviewPage`): every read answers the visual's saved
 * read (a table's first window, a matrix's first view), nothing is fetched, and each answered read is reported for the host's readiness.
 */
export const LINK_REPORT_ID = 'public-link';
export const LINK_PAGE_ID = 1;

export type LinkResult = {
    columns?: string[]; rows?: any[]; rowCount?: number; value?: unknown; formattingDomains?: unknown; matrix?: any;
    window?: { pageIndex: number; pageSize: number }; error?: string; code?: string; filtersNotApplied?: Array<{ label?: string | null; selection?: true }>;
    outputs?: Array<{ name: string; kind: string; canonicalType?: string; formatString?: string }>;
};

type Waiting = { uid: string; signal?: AbortSignal; resolve: (results: Record<string, LinkResult>) => void; reject: (error: unknown) => void };
const together = new Map<string, Waiting[]>();
const aborted = () => new DOMException('Aborted', 'AbortError');

/** One visual's plain read, sent with the others asked in the same tick under the same clicks (`targets`, one request). */
const readTogether = (interactionFilters: unknown[] | undefined, uid: string, signal?: AbortSignal) =>
    new Promise<Record<string, LinkResult>>((resolve, reject) => {
        const key = JSON.stringify([shown!.token, interactionFilters ?? null]);
        if (!together.size) setTimeout(sendTogether, 0);
        together.set(key, [...(together.get(key) ?? []), { uid, signal, resolve, reject }]);
    });

function sendTogether() {
    const groups = Array.from(together.entries());
    together.clear();
    for (const [key, waiting] of groups) {
        const [token, interactionFilters] = JSON.parse(key);
        const live = waiting.filter(item => !item.signal?.aborted || (item.reject(aborted()), false));
        if (live.length) void readGroup(token, interactionFilters ?? undefined, live);
    }
}

/** One grouped read. The visuals the server ran out of time for keep loading and are asked once more together, after 2-4 s; a second
 * `too_slow` is their answer. Signed-in reads never answer `too_slow` (they continue as read jobs), so their canvas needs no retry. */
async function readGroup(token: string, interactionFilters: unknown[] | undefined, live: Waiting[]) {
    // The request stops when every visual still waiting on it has stopped waiting.
    const controller = new AbortController();
    let open = live;
    for (const item of live) item.signal?.addEventListener('abort', () => {
        item.reject(aborted());
        if (open.every(other => other.signal?.aborted)) controller.abort();
    }, { once: true });
    const read = async (targets: string[]) => (await retrying(
        () => readData(token, { interactionFilters, targets }, undefined, controller.signal), controller.signal)).results as unknown as Record<string, LinkResult>;
    try {
        let results = await read(Array.from(new Set(live.map(item => item.uid))).sort());
        const slow = Array.from(new Set(live.map(item => item.uid).filter(uid => results[uid]?.code === 'too_slow'))).sort();
        if (slow.length) {
            live.filter(item => !slow.includes(item.uid)).forEach(item => item.resolve(results));
            open = live.filter(item => slow.includes(item.uid));
            if (open.every(item => item.signal?.aborted)) return;
            await sleep(2000 + Math.random() * 2000, controller.signal);
            results = { ...results, ...await read(slow) };
        }
        open.forEach(item => item.resolve(results));
    } catch (error) {
        open.forEach(item => item.reject(error));
    }
}

type Preview = { results: Record<string, LinkResult>; onRead: (uid: string) => void };
let shown: { token: string; page: LinkPage; preview?: Preview } | null = null;
/** The page the canvas is drawing and the link it came from; the reads below answer for it (only a canvas that names `LINK_REPORT_ID` asks). */
export const showLinkPage = (token: string, page: LinkPage) => { shown = { token, page }; };
/** A native page in the preview host: the reads answer from the snapshot's `results`; `onRead` hears each visual answered. */
export const showPreviewPage = (page: LinkPage, results: Record<string, LinkResult>, onRead: (uid: string) => void) => {
    shown = { token: '', page, preview: { results, onRead } };
};
export const isLinkRead = (reportId: unknown) => reportId === LINK_REPORT_ID && shown !== null;

/** A table's header sort in the link's visit: kept with the visit's filter values, so the pane's Reset clears it and its marker counts it.
 * `subscribeLinkSorts` is for `useSyncExternalStore`. */
export const subscribeLinkSorts = subscribeVisit;
export const linkTableSort = (visualUid: string | undefined) => shown && visualUid ? visitSort(shown.token, visualUid) : null;
export const setLinkTableSort = (visualUid: string, sort: TableSort | null) => { if (shown) setVisitSort(shown.token, visualUid, sort); };

/** The page shell the canvas reads first: one entry per visual, with a number the canvas keys it by (the link names visuals by uid only). */
export const linkManifest = () => {
    const { page } = shown!;
    const visuals = page.visuals.map((visual, index) => ({
        id: index + 1, visualUid: visual.visualUid, visualType: visual.type, title: visual.title ?? '', position: visual.position as any,
        customization: visual.appearance, rendererSpec: visual.rendererSpec, matrixSpec: visual.matrixSpec, subtitle: visual.subtitle ?? undefined,
        shapeType: visual.shapeType ?? undefined, revision: String(page.version),
        columnConfig: visual.columns ? { columns: visual.columns } : undefined,
    }));
    const data: VisualShellManifestResponse = { scope: 'pages', page: { pageId: LINK_PAGE_ID, lockedViewport: page.canvas }, visuals };
    return { data };
};

/** The visualUid of a visual the canvas names by its number (or by uid). */
export const linkVisualUid = (raw: unknown) => {
    const text = String(raw ?? '');
    return /^\d+$/.test(text) ? shown!.page.visuals[Number(text) - 1]?.visualUid : text.toLowerCase();
};

/** One visual's read, asked of the link in the words the canvas asks the signed-in routes, answered in the words it reads them in. */
export async function readLinkVisual(url: string, params: Record<string, any>, signal?: AbortSignal) {
    const kind = Object.keys(VISUAL_DATA_ENDPOINTS).find(key => VISUAL_DATA_ENDPOINTS[key as keyof typeof VISUAL_DATA_ENDPOINTS].endpoint === url)!;
    const uid = linkVisualUid(params[VISUAL_DATA_ENDPOINTS[kind as keyof typeof VISUAL_DATA_ENDPOINTS].param]);
    const visual = shown!.page.visuals.find(item => item.visualUid === uid);
    if (!visual) throw new Error('Visual unavailable in this report');
    if (visual.unavailable) return { data: { sql_data: { columns: [], rows: [], error: friendlyError('source_unavailable') } } };
    // A click names the clicked visual by the number the canvas keys it by; the link names visuals by uid.
    const interactionFilters = params.interactionFilters
        ? JSON.parse(params.interactionFilters).map((click: any) => ({...click, sourceVisualId: linkVisualUid(click.sourceVisualId)}))
        : undefined;
    // A table's header sort is the visit's (one output, never stored); the run of a matrix belongs to the server.
    const sort = params.sort ? JSON.parse(params.sort) : null;
    const window = kind === 'dataTable'
        ? { pageIndex: Number(params.pageIndex) || 0, pageSize: Number(params.pageSize) || undefined,
            sorting: sort ? [{ id: sort.name, desc: sort.direction === 'desc' }] : undefined }
        : undefined;
    const view = params.matrixView ? (({ token: _run, ...rest }) => rest)(JSON.parse(params.matrixView)) : undefined;
    let results: Record<string, LinkResult>;
    try {
        results = shown!.preview?.results ?? (window || view
            ? (await retrying(() => readData(shown!.token, { interactionFilters, window, view }, uid, signal), signal ?? new AbortController().signal)).results as unknown as Record<string, LinkResult>
            : await readTogether(interactionFilters, uid!, signal));
    } catch (error) {
        if (error instanceof PublicLinkError && error.busy) return { data: { sql_data: { columns: [], rows: [], error: friendlyError(error.code) } } };
        throw error;
    }
    const result = results[uid!];
    shown!.preview?.onRead(uid!);
    if (!result) throw new Error('No data returned for this visual.');
    if (result.error) return { data: { sql_data: { columns: [], rows: [], error: friendlyError(result.code, result.error) } } };
    const { columns = [], rows = [], value, matrix, formattingDomains } = result;
    return { data: {
        sql_data: { columns, rows, value, matrix, formatting_domains: formattingDomains }, value, outputs: result.outputs,
        total_rows: result.rowCount, page_index: result.window?.pageIndex, page_size: result.window?.pageSize,
        rendererSpec: visual.rendererSpec, matrixSpec: visual.matrixSpec,
        // The visit's own click that missed this visual is the selection's entry, as the signed-in read lists it.
        filtersNotApplied: result.filtersNotApplied?.map(entry => entry.selection ? { filterUid: null } : entry),
    } };
}
